import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { useToast } from '../../components/toastContext'
import { downloadCertificates, downloadUsersList, listIssuedTo } from './printService'
import { strings as S } from './strings'
import { serverDetail } from '../../lib/i18n'

/**
 * Who holds one programme's certificate, which of them are ticked, and the two downloads —
 * shared by طباعة الشهادات and the programme report's certificates panel, which the legacy
 * product built twice over the same three endpoints.
 *
 * @param {object} [program]  `{ value, label }` — the roster file is named after it.
 * @param {string|null} [rememberKey]  where the ticks survive leaving the screen; null keeps
 *   them for this visit only.
 */
export default function useIssuedCertificates({ programId, program, fromDate = '', toDate = '', rememberKey = null }) {
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState('')
  const [selected, setSelected] = useRemembered(rememberKey, [])
  const [busy, setBusy] = useState(false)

  /* Nothing is fetched until a programme is chosen — the endpoint is keyed by one, and
     asking without it returns a refusal rather than everything. */
  useEffect(() => {
    if (!programId) {
      setRows([])
      setNote('')
      return undefined
    }

    let cancelled = false
    setLoading(true)

    listIssuedTo({ entityId: programId, fromDate, toDate })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        /* The server's own sentence when a programme has issued none. */
        setNote(response.rows.length === 0 ? response.message : '')
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setNote('')
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })

    return () => {
      cancelled = true
    }
  }, [programId, fromDate, toDate, toast])

  /* The ticks belong to the list that was on screen, so a new programme or a new range
     drops them. It CHANGES the selection, it does not clear it on arrival — this would
     otherwise wipe what was just restored. See useOnChange. */
  useOnChange(() => setSelected([]), [programId, fromDate, toDate])

  const chosen = useMemo(
    () => rows.filter((row) => selected.includes(row.id)),
    [rows, selected],
  )

  const toggle = useCallback(
    (row) =>
      setSelected((previous) =>
        previous.includes(row.id)
          ? previous.filter((id) => id !== row.id)
          : [...previous, row.id],
      ),
    [setSelected],
  )

  /* Every row of the list, not only the page in view — a print run is the whole roster. */
  const selectAll = useCallback(
    (all) => setSelected(all ? rows.map((row) => row.id) : []),
    [rows, setSelected],
  )

  const run = async (call) => {
    setBusy(true)
    const outcome = await call
    setBusy(false)
    if (!outcome.ok) toast.error(serverDetail(outcome.message) || S.fail.print)
  }

  return {
    rows,
    loading,
    note,
    chosen,
    busy,
    isSelected: (row) => selected.includes(row.id),
    toggle,
    selectAll,
    downloadCertificates: () =>
      run(downloadCertificates({ entityId: programId, users: chosen })),
    downloadList: () => run(downloadUsersList({ program, users: chosen })),
  }
}
